import { useState } from 'react'
import { useStorage } from '../hooks/useStorage'
import { storageManager, sanitizeFilename } from '../lib/storage'

export function StorageTester() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploadPath, setUploadPath] = useState('')
  const [testResults, setTestResults] = useState<string[]>([])
  
  const {
    uploadFile,
    downloadFile,
    deleteFile,
    listFiles,
    getFileUrl,
    isLoading,
    error,
    uploadProgress,
    clearError
  } = useStorage({
    onSuccess: (result) => addResult(`✅ Success: ${JSON.stringify(result, null, 2)}`),
    onError: (error) => addResult(`❌ Error: ${error.message}`),
    onProgress: (progress) => addResult(`📊 Progress: ${progress}%`)
  })

  const addResult = (message: string) => {
    setTestResults(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`])
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setSelectedFile(file)
      const sanitizedName = sanitizeFilename(file.name)
      addResult(`📁 Selected file: ${file.name} → ${sanitizedName} (${file.size} bytes)`)
    }
  }

  const testUpload = async () => {
    if (!selectedFile) {
      addResult('❌ No file selected')
      return
    }

    addResult('🚀 Starting upload test...')
    const result = await uploadFile('videos', selectedFile, uploadPath || undefined)
    
    if (result) {
      addResult(`📤 Upload completed: ${result.path}`)
      // Test download
      addResult('⬇️ Testing download...')
      const blob = await downloadFile('videos', result.path)
      if (blob) {
        addResult(`📥 Download successful: ${blob.size} bytes`)
      }
    }
  }

  const testListFiles = async () => {
    addResult('📋 Listing files in videos bucket...')
    const files = await listFiles('videos')
    addResult(`📁 Found ${files.length} files`)
    files.forEach(file => {
      addResult(`  - ${file.name} (${file.created_at})`)
    })
  }

  const testGetUrl = async () => {
    if (!uploadPath) {
      addResult('❌ No path specified for URL test')
      return
    }

    addResult('🔗 Testing URL generation...')
    const publicUrl = await getFileUrl('videos', uploadPath, false)
    const signedUrl = await getFileUrl('videos', uploadPath, true, 3600)
    
    if (publicUrl) addResult(`🌐 Public URL: ${publicUrl}`)
    if (signedUrl) addResult(`🔐 Signed URL: ${signedUrl}`)
  }

  const testDelete = async () => {
    if (!uploadPath) {
      addResult('❌ No path specified for delete test')
      return
    }

    addResult('🗑️ Testing file deletion...')
    const success = await deleteFile('videos', uploadPath)
    if (success) {
      addResult('✅ File deleted successfully')
    }
  }

  const testStorageStats = async () => {
    addResult('📊 Getting storage statistics...')
    try {
      const stats = await storageManager.getStorageStats()
      addResult(`📈 Storage stats: ${JSON.stringify(stats, null, 2)}`)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
      addResult(`❌ Failed to get stats: ${errorMessage}`)
    }
  }

  const clearResults = () => {
    setTestResults([])
    clearError()
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="card">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Storage Connection Tester</h2>
        
        {/* File Selection */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Test File
            </label>
            <input
              type="file"
              onChange={handleFileSelect}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload Path (optional)
            </label>
            <input
              type="text"
              value={uploadPath}
              onChange={(e) => setUploadPath(e.target.value)}
              placeholder="e.g., test/video.mp4"
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Test Buttons */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-6">
          <button
            onClick={testUpload}
            disabled={!selectedFile || isLoading}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Testing...' : 'Test Upload'}
          </button>

          <button
            onClick={testListFiles}
            disabled={isLoading}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            List Files
          </button>

          <button
            onClick={testGetUrl}
            disabled={!uploadPath || isLoading}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Test URLs
          </button>

          <button
            onClick={testDelete}
            disabled={!uploadPath || isLoading}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Test Delete
          </button>

          <button
            onClick={testStorageStats}
            disabled={isLoading}
            className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Storage Stats
          </button>

          <button
            onClick={clearResults}
            className="btn-secondary"
          >
            Clear Results
          </button>
        </div>

        {/* Progress and Error Display */}
        {uploadProgress > 0 && uploadProgress < 100 && (
          <div className="mt-4">
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div 
                className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" 
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <p className="text-sm text-gray-600 mt-1">Upload Progress: {uploadProgress}%</p>
          </div>
        )}

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-800 text-sm">{error.message}</p>
          </div>
        )}
      </div>

      {/* Test Results */}
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Test Results</h3>
          <span className="text-sm text-gray-500">{testResults.length} results</span>
        </div>
        
        <div className="bg-gray-50 rounded-md p-4 max-h-96 overflow-y-auto">
          {testResults.length === 0 ? (
            <p className="text-gray-500 text-center">No test results yet. Run some tests to see results here.</p>
          ) : (
            <div className="space-y-2">
              {testResults.map((result, index) => (
                <div key={index} className="text-sm font-mono bg-white p-2 rounded border">
                  {result}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* File Naming Info */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">📝 File Naming Guidelines</h3>
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <p className="text-blue-800 text-sm mb-2">
            <strong>File names are automatically sanitized for compatibility:</strong>
          </p>
          <ul className="text-blue-700 text-xs space-y-1">
            <li>• Spaces are replaced with underscores (_)</li>
            <li>• Special characters are removed (except dots, dashes, underscores)</li>
            <li>• Multiple underscores are condensed to single ones</li>
            <li>• Example: "Gravação de Tela 2025-08-22 032453.mp4" → "Gravacao_de_Tela_2025-08-22_032453.mp4"</li>
          </ul>
        </div>
      </div>
    </div>
  )
}
